import type { ApiAccessClient, WorkflowType } from '@communities/content-management/domain';
import { useCommunityWorkflows, useUpsertCommunityWorkflow } from '@communities/content-management/hooks';
import { DataList, type DeleteItemContext, type TableColumn } from '@core/components';
import { SectionHeader } from '@core/components';
import { Text, Badge, IconButton, Info16, IconButtonSize, IconSize, Button } from '@soteria/design-system-ve';
import { useCallback, useState } from 'react';

import { ProviderApplicationDialog } from './ProviderApplicationDialog';
import { ProviderApplicationsMoreInfoDialog } from './ProviderApplicationsMoreInfoDialog';

interface ProviderApplicationsSectionProps {
  communityId: string;
  workflowType: WorkflowType;
  isCommunityAdmin: boolean;
}

export function ProviderApplicationsSection({ communityId, workflowType, isCommunityAdmin }: ProviderApplicationsSectionProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);
  const [selectedProviderApplication, setSelectedProviderApplication] = useState<ApiAccessClient | null>(null);

  const { workflows, loading, errors } = useCommunityWorkflows(communityId, workflowType);
  const { upsertWorkflow, loading: isDeleting, errors: deleteErrors, resetErrors } = useUpsertCommunityWorkflow();

  const accessClients = workflows?.apiSettings?.accessClients || [];

  const handleOpenDialog = useCallback((provider?: ApiAccessClient) => {
    setSelectedProviderApplication(provider || null);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProviderApplication(null);
  };

  const handleDeleteProvider = async (provider: ApiAccessClient): Promise<boolean> => {
    const index = accessClients.findIndex(p => p.providerUrl === provider.providerUrl);

    const updatedClients = accessClients.filter((_, i) => i !== index);
    // Create workflow configuration (handles both new and existing workflows)
    const updatedWorkflow = {
      type: workflowType,
      topicActions: workflows?.topicActions || [],
      apiSettings: {
        maxTps: workflows?.apiSettings?.maxTps || 100,
        accessClients: updatedClients,
      },
    };

    return await upsertWorkflow(communityId, workflowType, updatedWorkflow);
  };

  // Create the delete item context
  const deleteItemContext: DeleteItemContext<ApiAccessClient> = {
    handler: handleDeleteProvider,
    isDeleting,
    errors: deleteErrors,
    resetErrors,
  };

  // Define table columns for provider applications
  const columns: TableColumn<ApiAccessClient>[] = [
    {
      key: 'provider',
      header: 'Provider',
      render: (_, index) => <Text>Provider {(index || 0) + 1}</Text>,
      width: '20%',
    },
    {
      key: 'url',
      header: 'Provider URL',
      render: provider => <Text>{provider.providerUrl || 'No URL configured'}</Text>,
      width: '40%',
    },
    {
      key: 'clientIds',
      header: 'Client IDs',
      render: provider => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
          {provider.clientIdList.slice(0, 3).map((clientId, _index) => (
            <Badge key={`${clientId || 'empty'}-${Math.random()}`} text={clientId || 'Empty'} />
          ))}
          {provider.clientIdList.length > 3 && <Badge text={`+${provider.clientIdList.length - 3} more`} />}
        </div>
      ),
      width: '40%',
    },
  ];

  return (
    <div>
      <SectionHeader
        description="Manage OAuth2 client applications authorized to access your community's API endpoints."
        endComponent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconButton
              aria-label="Learn more about API client applications"
              buttonSize={IconButtonSize.Medium}
              icon={Info16}
              iconSize={IconSize.XSmall}
              onClick={() => setIsMoreInfoDialogOpen(true)}
            />
            {isCommunityAdmin && <Button onClick={() => handleOpenDialog()}>ADD PROVIDER</Button>}
          </div>
        }
        title={`${'API Client Applications'} (${accessClients.length})`}
      />

      {/* Provider Applications List */}
      <DataList
        columns={columns}
        data={accessClients}
        dataTypeName="provider application"
        deleteItemContext={isCommunityAdmin ? deleteItemContext : undefined}
        error={errors}
        isLoading={loading}
        onEdit={isCommunityAdmin ? handleOpenDialog : undefined}
      />

      {/* Create/Edit Provider Application Dialog */}
      <ProviderApplicationDialog
        communityId={communityId}
        isOpen={isDialogOpen}
        provider={selectedProviderApplication}
        workflowType={workflowType}
        onClose={handleCloseDialog}
      />

      {/* More Info Dialog */}
      <ProviderApplicationsMoreInfoDialog isOpen={isMoreInfoDialogOpen} workflowType={workflowType} onClose={() => setIsMoreInfoDialogOpen(false)} />
    </div>
  );
}
